import { LOGGED_EMAIL_CHANNEL_ID, ORG_DOMAINS, TWENTY_BASE_URL } from './config';
import { DEFAULT_STAGE } from './categories';
import { resolveContact } from './office';
import type {
  CompanyRecord,
  CrmMatch,
  CurrentEmail,
  EmailDirection,
  EmailParticipant,
  PersonRecord,
} from './types';

// Twenty data API calls (GraphQL), authenticated as the signed-in user.
// Logged emails are written as NATIVE Twenty messages (message / messageThread /
// messageParticipant) associated to the dedicated "Logged via Outlook" channel,
// so they show in the built-in Emails tab. (Replaces the old custom LoggedEmail
// object.) Members can write these: every role has canUpdateAllObjectRecords.

const PERSONAL_DOMAINS = new Set([
  'gmail.com',
  'outlook.com',
  'hotmail.com',
  'yahoo.com',
  'icloud.com',
  'me.com',
  'aol.com',
  'protonmail.com',
  'proton.me',
]);

const graphql = async <TData>(
  token: string,
  query: string,
  variables: Record<string, unknown>,
): Promise<TData> => {
  const response = await fetch(`${TWENTY_BASE_URL}/graphql`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ query, variables }),
  });
  const payload = (await response.json()) as {
    data?: TData;
    errors?: { message: string }[];
  };
  if (payload.errors?.length) throw new Error(payload.errors[0].message);
  if (!payload.data) throw new Error('Empty GraphQL response');
  return payload.data;
};

// Strip scheme/www/path so a stored URL like "https://seaopssolutions.com"
// compares equal to a bare domain "seaopssolutions.com".
const normalizeHost = (url: string): string =>
  url
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/.*$/, '')
    .toLowerCase();

type EdgeList<T> = { edges: { node: T }[] };

// A match is looked up live first, then in the trash. Why the second pass:
// Twenty's read queries hide soft-deleted rows, but its email/domain UNIQUE
// indexes still COUNT them. So if a contact sits in the trash, a live find
// returns nothing and a naive re-create collides ("A duplicate entry was
// detected"). Finding the trashed row and restoring it on log avoids that.
const TRASH_FILTER = ', deletedAt: { is: NOT_NULL }';

type PersonNode = {
  id: string;
  name: { firstName: string; lastName: string };
  emails: { primaryEmail: string } | null;
  jobTitle: string | null;
  phones: {
    primaryPhoneCallingCode: string | null;
    primaryPhoneCountryCode: string | null;
    primaryPhoneNumber: string | null;
  } | null;
  company: { name: string } | null;
  category: string[] | null;
};

const mapPerson = (node: PersonNode, deleted: boolean): PersonRecord => ({
  id: node.id,
  firstName: node.name.firstName ?? '',
  lastName: node.name.lastName ?? '',
  email: node.emails?.primaryEmail ?? '',
  jobTitle: node.jobTitle ?? '',
  phone: node.phones?.primaryPhoneNumber ?? '',
  phoneCallingCode: node.phones?.primaryPhoneCallingCode ?? '',
  phoneCountryCode: node.phones?.primaryPhoneCountryCode ?? '',
  companyName: node.company?.name ?? null,
  category: node.category ?? [],
  deleted,
});

export const findPersonByEmail = async (
  token: string,
  email: string,
): Promise<PersonRecord | null> => {
  // ilike (not eq) so the match is case-insensitive.
  const run = async (trash: boolean): Promise<PersonNode | null> => {
    const data = await graphql<{ people: EdgeList<PersonNode> }>(
      token,
      `query ($e: String!) {
        people(filter: { emails: { primaryEmail: { ilike: $e } }${trash ? TRASH_FILTER : ''} }, first: 1) {
          edges { node {
            id name { firstName lastName } emails { primaryEmail } jobTitle
            phones { primaryPhoneCallingCode primaryPhoneCountryCode primaryPhoneNumber } company { name }
            category
          } }
        }
      }`,
      { e: email },
    );
    return data.people.edges[0]?.node ?? null;
  };
  const live = await run(false);
  if (live) return mapPerson(live, false);
  const trashed = await run(true);
  return trashed ? mapPerson(trashed, true) : null;
};

type CompanyNode = {
  id: string;
  name: string;
  domainName: { primaryLinkUrl: string } | null;
  address: { addressCity: string | null } | null;
  category: string[] | null;
  lifecycleStage: string | null;
};

const mapCompany = (node: CompanyNode, deleted: boolean): CompanyRecord => ({
  id: node.id,
  name: node.name,
  // normalize to the bare host so the display matches new-company prefill
  domain: normalizeHost(node.domainName?.primaryLinkUrl ?? ''),
  city: node.address?.addressCity ?? '',
  category: node.category ?? [],
  lifecycleStage: node.lifecycleStage ?? DEFAULT_STAGE,
  deleted,
});

export const findCompanyByDomain = async (
  token: string,
  domain: string,
): Promise<CompanyRecord | null> => {
  // Domains are stored as full URLs (e.g. "https://x.com") by the calendar
  // sync, so match by ilike on the bare domain and verify the host in JS to
  // avoid both misses and false positives (e.g. "notx.com").
  const run = async (trash: boolean): Promise<CompanyNode | null> => {
    const data = await graphql<{ companies: EdgeList<CompanyNode> }>(
      token,
      `query ($d: String!) {
        companies(filter: { domainName: { primaryLinkUrl: { ilike: $d } }${trash ? TRASH_FILTER : ''} }, first: 5) {
          edges { node { id name domainName { primaryLinkUrl } address { addressCity } category lifecycleStage } }
        }
      }`,
      { d: `%${domain}` },
    );
    return (
      data.companies.edges
        .map((edge) => edge.node)
        .find((node) => normalizeHost(node.domainName?.primaryLinkUrl ?? '') === domain) ?? null
    );
  };
  const live = await run(false);
  if (live) return mapCompany(live, false);
  const trashed = await run(true);
  return trashed ? mapCompany(trashed, true) : null;
};

// Bring a soft-deleted record back to live so it can be linked/edited instead
// of re-created. Called on the explicit log/save action, never on mere view.
export const restorePerson = async (token: string, id: string): Promise<void> => {
  await graphql(
    token,
    `mutation ($id: UUID!) { restorePerson(id: $id) { id } }`,
    { id },
  );
};

export const restoreCompany = async (token: string, id: string): Promise<void> => {
  await graphql(
    token,
    `mutation ($id: UUID!) { restoreCompany(id: $id) { id } }`,
    { id },
  );
};

type WithChannels = {
  messageChannelMessageAssociations: EdgeList<{ messageChannelId: string }>;
};

// A message "counts" as logged-by-us only if it's associated to OUR logging
// channel. This is the guard that stops a mailbox-synced or native-composer
// message (same Message-ID, different channel) from making the add-in think an
// email is already logged - the bug that once showed every email as "logged".
const isOnLoggingChannel = (node: WithChannels): boolean =>
  node.messageChannelMessageAssociations.edges.some(
    (e) => e.node.messageChannelId === LOGGED_EMAIL_CHANNEL_ID,
  );

const CHANNEL_SELECTION =
  'messageChannelMessageAssociations { edges { node { messageChannelId } } }';

// Native-message dedup, scoped to our logging channel.
const findMessageByHeaderId = async (
  token: string,
  headerMessageId: string,
): Promise<{ id: string; messageThreadId: string } | null> => {
  if (!headerMessageId) return null;
  const data = await graphql<{
    messages: EdgeList<{ id: string; messageThreadId: string } & WithChannels>;
  }>(
    token,
    `query ($h: String!) {
      messages(filter: { headerMessageId: { eq: $h } }, first: 5) {
        edges { node { id messageThreadId ${CHANNEL_SELECTION} } }
      }
    }`,
    { h: headerMessageId },
  );
  const node = data.messages.edges.map((e) => e.node).find(isOnLoggingChannel);
  return node ? { id: node.id, messageThreadId: node.messageThreadId } : null;
};

// Threading: reuse the thread of an already-logged ancestor (In-Reply-To /
// References) that is on OUR channel, so a reply groups with the original.
const findThreadIdByAncestors = async (
  token: string,
  ancestorIds: string[],
): Promise<string | null> => {
  const ids = Array.from(new Set(ancestorIds.filter(Boolean)));
  if (!ids.length) return null;
  const data = await graphql<{
    messages: EdgeList<{ messageThreadId: string } & WithChannels>;
  }>(
    token,
    `query ($ids: [String!]) {
      messages(filter: { headerMessageId: { in: $ids } }, first: 20) {
        edges { node { messageThreadId ${CHANNEL_SELECTION} } }
      }
    }`,
    { ids },
  );
  return (
    data.messages.edges.map((e) => e.node).find(isOnLoggingChannel)
      ?.messageThreadId ?? null
  );
};

// Manual edits saved straight to a matched record.
export const updatePerson = async (
  token: string,
  id: string,
  fields: {
    firstName: string;
    lastName: string;
    jobTitle: string;
    email: string;
    phoneNumber: string;
    phoneCallingCode: string;
    phoneCountryCode: string;
    category: string[];
  },
): Promise<void> => {
  const data: Record<string, unknown> = {
    name: { firstName: fields.firstName, lastName: fields.lastName },
    jobTitle: fields.jobTitle,
    // A partial email update preserves additionalEmails (verified live).
    emails: { primaryEmail: fields.email },
    // MULTI_SELECT takes an array of option values; [] clears it.
    category: fields.category,
  };
  // Phone needs the FULL composite - a partial update is silently ignored.
  if (fields.phoneNumber) {
    data.phones = {
      primaryPhoneNumber: fields.phoneNumber,
      primaryPhoneCallingCode: fields.phoneCallingCode,
      primaryPhoneCountryCode: fields.phoneCountryCode,
    };
  }
  await graphql(
    token,
    `mutation ($id: UUID!, $d: PersonUpdateInput!) { updatePerson(id: $id, data: $d) { id } }`,
    { id, d: data },
  );
};

export const updateCompany = async (
  token: string,
  id: string,
  fields: { name: string; city: string; category: string[]; lifecycleStage: string },
): Promise<void> => {
  await graphql(
    token,
    `mutation ($id: UUID!, $d: CompanyUpdateInput!) { updateCompany(id: $id, data: $d) { id } }`,
    // A partial address update preserves the other address sub-fields (verified live).
    {
      id,
      d: {
        name: fields.name,
        address: { addressCity: fields.city },
        // MULTI_SELECT takes an array of option values; [] clears it.
        category: fields.category,
        lifecycleStage: fields.lifecycleStage,
      },
    },
  );
};

// Drives the taskpane match status. Uses resolveContact so platform/no-reply
// senders are matched against the real contact found in the signature.
export const getCrmMatch = async (
  token: string,
  email: CurrentEmail,
): Promise<CrmMatch> => {
  const guess = resolveContact(email);
  const contactEmail = guess.email;
  const domain = guess.domain || null;
  const internal = !!domain && ORG_DOMAINS.includes(domain);
  const canCreateCompany =
    !!domain && !internal && !PERSONAL_DOMAINS.has(domain);

  const [person, company, existingMessage] = await Promise.all([
    contactEmail && !internal
      ? findPersonByEmail(token, contactEmail)
      : Promise.resolve(null),
    canCreateCompany && domain
      ? findCompanyByDomain(token, domain)
      : Promise.resolve(null),
    findMessageByHeaderId(token, email.internetMessageId),
  ]);

  return {
    person,
    company,
    alreadyLogged: !!existingMessage,
    internal,
    contactEmail,
    domain: domain ?? '',
    guessPhone: guess.phone,
    automatedSender: guess.automatedSender,
    fromSignature: guess.fromSignature,
    canCreateCompany,
  };
};

export type NewPersonFields = {
  firstName: string;
  lastName: string;
  email: string;
  jobTitle: string;
  phoneNumber: string;
  category: string[];
};

export type NewCompanyFields = {
  name: string;
  domain: string;
  city: string;
  category: string[];
  lifecycleStage: string;
};

const sameEmail = (a: string, b: string): boolean =>
  !!a && !!b && a.toLowerCase() === b.toLowerCase();

const linkPersonToCompany = async (
  token: string,
  personId: string,
  companyId: string,
): Promise<void> => {
  await graphql(
    token,
    `mutation ($id: UUID!, $d: PersonUpdateInput!) { updatePerson(id: $id, data: $d) { id } }`,
    { id: personId, d: { companyId } },
  );
};

const createMessageThread = async (token: string, subject: string): Promise<string> => {
  const data = await graphql<{ createMessageThread: { id: string } }>(
    token,
    `mutation ($d: MessageThreadCreateInput!) { createMessageThread(data: $d) { id } }`,
    { d: { subject } },
  );
  return data.createMessageThread.id;
};

const createMessageRecord = async (
  token: string,
  input: Record<string, unknown>,
): Promise<string> => {
  const data = await graphql<{ createMessage: { id: string } }>(
    token,
    `mutation ($d: MessageCreateInput!) { createMessage(data: $d) { id } }`,
    { d: input },
  );
  return data.createMessage.id;
};

const createParticipant = async (
  token: string,
  input: Record<string, unknown>,
): Promise<void> => {
  await graphql(
    token,
    `mutation ($d: MessageParticipantCreateInput!) { createMessageParticipant(data: $d) { id } }`,
    { d: input },
  );
};

const createChannelAssociation = async (
  token: string,
  input: Record<string, unknown>,
): Promise<void> => {
  await graphql(
    token,
    `mutation ($d: MessageChannelMessageAssociationCreateInput!) { createMessageChannelMessageAssociation(data: $d) { id } }`,
    { d: input },
  );
};

export type LogInput = {
  loggerNote?: string;
  personId: string | null; // link an existing person
  newPerson: NewPersonFields | null; // or create one
  companyId: string | null; // link an existing company
  newCompany: NewCompanyFields | null; // or create one
  personHasCompany: boolean; // matched person already has an employer (don't relink)
};

// Resolve (create/link) the person + company for a log action. Company first so
// a newly-created person links to it on creation, which surfaces the email on
// the company's Emails tab too (native aggregates company mail via its people).
const resolvePersonAndCompany = async (
  token: string,
  input: LogInput,
): Promise<{ personId: string | null; companyId: string | null }> => {
  let companyId = input.companyId;
  if (!companyId && input.newCompany) {
    const company = input.newCompany;
    const data: Record<string, unknown> = {
      name: company.name || company.domain,
      domainName: { primaryLinkUrl: `https://${company.domain}`, secondaryLinks: [] },
      lifecycleStage: company.lifecycleStage,
    };
    if (company.category.length) data.category = company.category;
    if (company.city) data.address = { addressCity: company.city };
    const created = await graphql<{ createCompany: { id: string } }>(
      token,
      `mutation ($d: CompanyCreateInput!) { createCompany(data: $d) { id } }`,
      { d: data },
    );
    companyId = created.createCompany.id;
  }

  let personId = input.personId;
  if (!personId && input.newPerson) {
    const person = input.newPerson;
    const data: Record<string, unknown> = {
      name: { firstName: person.firstName, lastName: person.lastName },
      emails: { primaryEmail: person.email, additionalEmails: [] },
    };
    if (person.jobTitle) data.jobTitle = person.jobTitle;
    if (person.category.length) data.category = person.category;
    if (companyId) data.companyId = companyId;
    if (person.phoneNumber) {
      data.phones = {
        primaryPhoneNumber: person.phoneNumber,
        primaryPhoneCallingCode: '',
        primaryPhoneCountryCode: '',
      };
    }
    const created = await graphql<{ createPerson: { id: string } }>(
      token,
      `mutation ($d: PersonCreateInput!) { createPerson(data: $d) { id } }`,
      { d: data },
    );
    personId = created.createPerson.id;
  } else if (personId && companyId && !input.personHasCompany) {
    await linkPersonToCompany(token, personId, companyId);
  }

  return { personId, companyId };
};

// The subset both a single CurrentEmail and a Graph conversation message satisfy
// - enough to write one native message graph.
export type WritableMessage = {
  subject: string;
  body: string;
  sentAt: string | null;
  internetMessageId: string;
  inReplyTo: string;
  references: string[];
  direction: EmailDirection;
  participants: EmailParticipant[];
};

// Write one native message (dedup -> message -> participants -> association) onto
// the given thread, linking the contact person. created=false means it was
// already logged (skipped) - this is what makes thread-logging idempotent.
const writeMessageGraph = async (
  token: string,
  msg: WritableMessage,
  opts: { threadId: string; personId: string | null; contactEmail: string; note?: string },
): Promise<{ messageId: string; created: boolean }> => {
  const existing = await findMessageByHeaderId(token, msg.internetMessageId);
  if (existing) return { messageId: existing.id, created: false };

  const note = opts.note ? `\n\n— Logged note —\n${opts.note}` : '';
  const messageId = await createMessageRecord(token, {
    subject: msg.subject,
    text: `${msg.body}${note}`,
    receivedAt: msg.sentAt,
    headerMessageId: msg.internetMessageId || null,
    messageThreadId: opts.threadId,
  });

  // Attach the contact Person to the participant that matches the resolved
  // contact email, so the message appears on their record. For the
  // no-reply/signature case the contact isn't a header participant, so add it.
  let linkedContact = false;
  for (const participant of msg.participants) {
    const isContact = !!opts.personId && sameEmail(participant.email, opts.contactEmail);
    if (isContact) linkedContact = true;
    await createParticipant(token, {
      role: participant.role,
      handle: participant.email,
      displayName: participant.name || null,
      messageId,
      personId: isContact ? opts.personId : null,
    });
  }
  if (opts.personId && !linkedContact) {
    await createParticipant(token, {
      role: msg.direction === 'INBOUND' ? 'FROM' : 'TO',
      handle: opts.contactEmail,
      displayName: null,
      messageId,
      personId: opts.personId,
    });
  }

  await createChannelAssociation(token, {
    messageChannelId: LOGGED_EMAIL_CHANNEL_ID,
    messageId,
    messageThreadId: opts.threadId,
    direction: msg.direction === 'INBOUND' ? 'INCOMING' : 'OUTGOING',
  });

  return { messageId, created: true };
};

// Log the single open email as a native Twenty message graph. A reply groups
// with an already-logged original via its In-Reply-To/References ancestors.
export const logEmail = async (
  token: string,
  email: CurrentEmail,
  input: LogInput,
): Promise<{ messageId: string }> => {
  const { personId } = await resolvePersonAndCompany(token, input);
  const contactEmail = resolveContact(email).email;
  const threadId =
    (await findThreadIdByAncestors(token, [email.inReplyTo, ...email.references])) ??
    (await createMessageThread(token, email.subject));
  const { messageId } = await writeMessageGraph(token, email, {
    threadId,
    personId,
    contactEmail,
    note: input.loggerNote,
  });
  return { messageId };
};

// Log every message of a conversation (fetched from Graph) into ONE shared
// thread, linked to the same contact. Idempotent - already-logged messages are
// skipped. `email` is the open message (drives contact resolution + subject).
export const logThread = async (
  token: string,
  email: CurrentEmail,
  messages: WritableMessage[],
  input: LogInput,
): Promise<{ logged: number; total: number }> => {
  const { personId } = await resolvePersonAndCompany(token, input);
  const contactEmail = resolveContact(email).email;

  // One thread for the whole conversation: reuse an already-logged ancestor's
  // thread if there is one, else start a fresh thread under the open subject.
  const ancestorIds = messages.flatMap((m) => [
    m.internetMessageId,
    m.inReplyTo,
    ...m.references,
  ]);
  const threadId =
    (await findThreadIdByAncestors(token, ancestorIds)) ??
    (await createMessageThread(token, email.subject));

  let logged = 0;
  for (const msg of messages) {
    const { created } = await writeMessageGraph(token, msg, {
      threadId,
      personId,
      contactEmail,
    });
    if (created) logged += 1;
  }
  return { logged, total: messages.length };
};

// --- Compose surface: email templates + tracked-send engagement records -------
// These consume the email-engagement backend (emailTemplate object + loggedEmail
// click-tracking fields + the /s/track/c route). A tracked send is recorded as a
// loggedEmail (the backend's engagement model), distinct from the native-message
// logging above - a tracked send carries no Message-ID until it's actually sent.

export type EmailTemplate = {
  id: string;
  name: string;
  subject: string;
  body: string;
  category: string | null;
};

export const fetchTemplates = async (token: string): Promise<EmailTemplate[]> => {
  const data = await graphql<{
    emailTemplates: EdgeList<{
      id: string;
      name: string;
      subject: string | null;
      body: string | null;
      category: string | null;
    }>;
  }>(
    token,
    `query {
      emailTemplates(first: 200, orderBy: { name: AscNullsLast }) {
        edges { node { id name subject body category } }
      }
    }`,
    {},
  );
  return data.emailTemplates.edges.map((e) => ({
    id: e.node.id,
    name: e.node.name ?? '',
    subject: e.node.subject ?? '',
    body: e.node.body ?? '',
    category: e.node.category ?? null,
  }));
};

export type TrackedEmailInput = {
  subject: string;
  fromEmail: string;
  toEmails: string[];
  body: string;
  sentAt: string; // ISO 8601
  token: string; // tracking token embedded in the rewritten links
  personId: string | null;
  companyId: string | null;
  loggerNote?: string;
};

// Create the engagement record for a tracked send. engagementStatus starts at
// SENT; the public track-click route flips it to CLICKED and bumps clickCount.
export const createTrackedEmail = async (
  token: string,
  input: TrackedEmailInput,
): Promise<{ id: string }> => {
  const data: Record<string, unknown> = {
    subject: input.subject || '(no subject)',
    fromEmail: input.fromEmail,
    toEmails: input.toEmails.join(', '),
    body: input.body,
    sentAt: input.sentAt,
    direction: 'OUTBOUND',
    trackingToken: input.token,
    engagementStatus: 'SENT',
    clickCount: 0,
  };
  if (input.loggerNote) data.loggerNote = input.loggerNote;
  if (input.personId) data.personId = input.personId;
  if (input.companyId) data.companyId = input.companyId;
  const res = await graphql<{ createLoggedEmail: { id: string } }>(
    token,
    `mutation ($d: LoggedEmailCreateInput!) { createLoggedEmail(data: $d) { id } }`,
    { d: data },
  );
  return res.createLoggedEmail;
};

// Re-tracking the same draft: refresh the metadata (subject/recipients/body may
// have changed) but keep the token and do NOT touch engagementStatus/clickCount
// (a click may already have landed).
export const updateTrackedEmail = async (
  token: string,
  id: string,
  input: Pick<TrackedEmailInput, 'subject' | 'toEmails' | 'body' | 'personId' | 'companyId' | 'loggerNote'>,
): Promise<void> => {
  const data: Record<string, unknown> = {
    subject: input.subject || '(no subject)',
    toEmails: input.toEmails.join(', '),
    body: input.body,
  };
  if (input.loggerNote) data.loggerNote = input.loggerNote;
  if (input.personId) data.personId = input.personId;
  if (input.companyId) data.companyId = input.companyId;
  await graphql(
    token,
    `mutation ($id: UUID!, $d: LoggedEmailUpdateInput!) { updateLoggedEmail(id: $id, data: $d) { id } }`,
    { id, d: data },
  );
};
